import { BaseColumn, DsyHeaderCell, DsyRowCell, DsyRowData, DsyTableConf } from '../../models/dsy-table.model';

/**
 * Maps generic T array to {@link DsyRowData} dsy-table row entries.
 *
 * @param {readonly T[]} rows - The list of generic T to map.
 * @param {readonly BaseColumn<T,ColumnKey>[]} cols - The list of columns to apply.
 * @returns {DsyRowData[]} The mapped DSY table rows to render in dsy-table.
 */
export function mapRows<T extends Object, ColumnKey extends string>(rows: readonly T[], cols: readonly BaseColumn<T, ColumnKey>[]): DsyRowData[] {
  return rows.map(
    (log: T, idx: number) =>
      ({
        id: `row-${idx}`,
        cells: Object.fromEntries(
          cols.map((col: BaseColumn<T, ColumnKey>) => [
            col.key,
            {
              id: `cell-${idx}-${col.key}`,
              property: col.key,
              value: col.accessor(log),
            } as DsyRowCell,
          ])
        ),
      }) as DsyRowData
  );
}

/**
 * Builds the {@link DsyTableConf} configuration object for rendering in the T generic table.
 *
 * @param {readonly BaseColumn<T,ColumnKey>[]} cols - The column definitions for the table.
 * @param {readonly string[]} rowIds - The ids of each table row.
 * @returns {DsyTableConf} The complete configuration object for the DSY table.
 */
export function buildConf<T extends object, ColumnKey extends string>(cols: readonly BaseColumn<T, ColumnKey>[], rowIds: readonly string[]): DsyTableConf {
  return {
    styling: { table: { areRowsSeparated: true }, rows: { minHeight: '3.7rem', maxHeight: '3.7rem' } },
    renderOrder: {
      columns: { defaultOrder: cols.map(c => c.key) },
      rows: { defaultOrder: [...rowIds] },
    },
    headerRow: {
      id: 'header',
      cells: Object.fromEntries(
        cols.map(col => [
          col.key,
          {
            id: `header-${col.key}`,
            property: col.key,
            value: col.header,
          } as DsyHeaderCell,
        ])
      ) as Record<string, DsyHeaderCell>,
    },
  };
}
